// src/components/Account/PaymentHistory.jsx
import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

const getStatusBadge = (status = 'Completed', isEn = false) => {
  const normalized = String(status).toLowerCase();

  if (
    normalized.includes('complete') ||
    normalized.includes('success') ||
    normalized.includes('оплач') ||
    normalized.includes('успеш')
  ) {
    return (
      <span className="payment-status-pill completed">
        <span className="status-dot" />
        <span>{isEn ? 'Completed' : 'Оплачен'}</span>
      </span>
    );
  }

  if (
    normalized.includes('pend') ||
    normalized.includes('process') ||
    normalized.includes('обраб') ||
    normalized.includes('ожидан')
  ) {
    return (
      <span className="payment-status-pill pending">
        <span className="status-dot" />
        <span>{isEn ? 'Pending' : 'В обработке'}</span>
      </span>
    );
  }

  return (
    <span className="payment-status-pill failed">
      <span className="status-dot" />
      <span>{isEn ? 'Failed' : 'Отклонен'}</span>
    </span>
  );
};

const formatPaymentDate = (rawDate, isEn) => {
  if (!rawDate) return '—';
  try {
    const d = new Date(rawDate);
    if (!isNaN(d.getTime())) {
      return d.toLocaleString(isEn ? 'en-US' : 'ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    }
  } catch {}
  return String(rawDate);
};

const PaymentHistory = ({ payments = [] }) => {
  const { language } = useLanguage();
  const isEn = language === 'en';

  const safePayments = Array.isArray(payments) ? payments : [];

  return (
    <section className="module-section" id="paymentHistorySection">
      {/* Шапка секции недавних платежей */}
      <div className="module-header">
        <div className="module-title-group">
          <h2 className="module-title">
            {isEn ? 'Recent Payments' : 'Недавние платежи'}
          </h2>
          <p className="module-desc">
            {isEn
              ? 'Transaction history, subscription payments, and account balance operations.'
              : 'История транзакций, платежей за подписку и операций по балансу.'}
          </p>
        </div>
      </div>

      {/* Кибер-таблица платежей */}
      <div className="cyber-table-card">
        {safePayments.length === 0 ? (
          <div className="cyber-table-empty">
            <svg viewBox="0 0 24 24">
              <rect width="20" height="14" x="2" y="5" rx="2" />
              <line x1="2" y1="10" x2="22" y2="10" />
            </svg>
            <p>
              {isEn
                ? 'Payment history is empty. Your completed transactions and top-ups will appear here.'
                : 'История платежей пуста. Здесь будут отображаться ваши успешные транзакции и пополнения.'}
            </p>
          </div>
        ) : (
          <>
            {/* Десктопная таблица */}
            <div className="table-responsive-wrapper table-desktop-view">
              <table className="cyber-data-table">
                <thead>
                  <tr>
                    <th>{isEn ? 'Date' : 'Дата'}</th>
                    <th>{isEn ? 'Operation Description' : 'Описание операции'}</th>
                    <th>{isEn ? 'Amount' : 'Сумма'}</th>
                    <th>{isEn ? 'Payment Method' : 'Способ оплаты'}</th>
                    <th style={{ textAlign: 'right' }}>
                      {isEn ? 'Status' : 'Статус'}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {safePayments.map((p, i) => {
                    const dateVal = formatPaymentDate(p.date || p.createdAt, isEn);
                    const descVal =
                      p.description || p.title || (isEn ? 'Subscription payment' : 'Оплата подписки');
                    const rawAmount = p.amount !== undefined ? String(p.amount) : '0';
                    const amountVal =
                      rawAmount.includes('₽') || rawAmount.includes('$')
                        ? rawAmount
                        : `${rawAmount} ₽`;
                    const methodVal =
                      p.method ||
                      p.paymentMethod ||
                      p.gateway ||
                      (isEn ? 'Card / SBP' : 'Банковская карта / СБП');

                    return (
                      <tr key={p.id || i}>
                        <td>
                          <span
                            style={{
                              fontFamily: 'var(--acc-font-mono)',
                              fontSize: '0.82rem',
                              color: 'rgba(255, 255, 255, 0.65)',
                            }}
                          >
                            {dateVal}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div
                              style={{
                                width: 28,
                                height: 28,
                                borderRadius: '50%',
                                background: 'rgba(255, 255, 255, 0.05)',
                                border: '1px solid rgba(255, 255, 255, 0.12)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'rgba(255, 255, 255, 0.8)',
                                flexShrink: 0,
                              }}
                            >
                              <svg
                                viewBox="0 0 24 24"
                                style={{
                                  width: 13,
                                  height: 13,
                                  stroke: 'currentColor',
                                  strokeWidth: 2,
                                  fill: 'none',
                                }}
                              >
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14 2 14 8 20 8" />
                                <line x1="16" y1="13" x2="8" y2="13" />
                                <line x1="16" y1="17" x2="8" y2="17" />
                              </svg>
                            </div>
                            <span style={{ color: '#ffffff', fontWeight: 600 }}>
                              {descVal}
                            </span>
                          </div>
                        </td>
                        <td>
                          <span
                            className="cell-numeric"
                            style={{ fontSize: '0.92rem', color: '#ffffff' }}
                          >
                            {amountVal}
                          </span>
                        </td>
                        <td>
                          <span
                            style={{
                              color: 'rgba(255, 255, 255, 0.6)',
                              fontSize: '0.84rem',
                            }}
                          >
                            {methodVal}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          {getStatusBadge(p.status, isEn)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Мобильные адаптивные карточки */}
            <div className="mobile-cards-container">
              {safePayments.map((p, i) => {
                const dateVal = formatPaymentDate(p.date || p.createdAt, isEn);
                const descVal =
                  p.description || p.title || (isEn ? 'Subscription payment' : 'Оплата подписки');
                const rawAmount = p.amount !== undefined ? String(p.amount) : '0';
                const amountVal =
                  rawAmount.includes('₽') || rawAmount.includes('$')
                    ? rawAmount
                    : `${rawAmount} ₽`;
                const methodVal =
                  p.method ||
                  p.paymentMethod ||
                  p.gateway ||
                  (isEn ? 'Card / SBP' : 'Банковская карта / СБП');

                return (
                  <div key={p.id || i} className="mobile-data-card">
                    <div className="mobile-data-card-header">
                      <strong style={{ color: '#ffffff', fontSize: '0.92rem' }}>
                        {descVal}
                      </strong>
                      {getStatusBadge(p.status, isEn)}
                    </div>
                    <div className="mobile-data-card-row">
                      <span className="mobile-data-card-label">
                        {isEn ? 'Amount' : 'Сумма'}:
                      </span>
                      <span className="cell-numeric" style={{ fontSize: '1rem' }}>
                        {amountVal}
                      </span>
                    </div>
                    <div className="mobile-data-card-row">
                      <span className="mobile-data-card-label">
                        {isEn ? 'Date' : 'Дата'}:
                      </span>
                      <span
                        style={{
                          fontFamily: 'var(--acc-font-mono)',
                          fontSize: '0.80rem',
                          color: 'rgba(255, 255, 255, 0.65)',
                        }}
                      >
                        {dateVal}
                      </span>
                    </div>
                    <div className="mobile-data-card-row">
                      <span className="mobile-data-card-label">
                        {isEn ? 'Method' : 'Метод'}:
                      </span>
                      <span
                        style={{
                          fontSize: '0.80rem',
                          color: 'rgba(255, 255, 255, 0.5)',
                        }}
                      >
                        {methodVal}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default PaymentHistory;